/* eslint-disable no-unneeded-ternary */
import Vimeo from "@u-wave/react-vimeo";

import React, { useState } from "react";

import { observer } from "mobx-react";
import * as PropTypes from "prop-types";
import styled from "styled-components";

import Icon from "../../Icon/Icon";
import breakpoints from "../../constants";
import VideoOverlay from "./VideoOverlay";

const StyledPlayerWrapper = styled.div`
  position: relative;
  flex-grow: 1;
  z-index: 0;
  overflow: hidden;
  height: ${({ $height }) => $height};
  width: auto;

  iframe {
    width: 100vw !important;
    height: ${({ $height }) =>
      $height
        ? $height
        : "56.25vw"}!important; /* Given a 16:9 aspect ratio, 9/16*100 = 56.25 */
    min-height: ${({ $minHeight }) =>
      $minHeight ? $minHeight : "100vh"}!important;
    min-width: 177.77vh !important; /* Given a 16:9 aspect ratio, 16/9*100 = 177.77 */
    position: absolute;
    top: 50% !important;
    left: ${({ $videoHeroType }) =>
      $videoHeroType === "withCustomOverlay" ? "76%" : "50%"} !important;
    transform: translate(-50%, -50%);

    @media (max-width: ${breakpoints.MOBILE}) {
      min-width: unset !important;
    }
  }
`;

const StyledResponsiveWrapper = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
`;

const StyledVolumeWrapper = styled.div`
  position: absolute;
  z-index: 1;
  cursor: pointer;
  bottom: 1rem;
  right: 1rem;
`;

const StyledContentWrapper = styled.div`
  color: ${({ theme }) => theme.negativeTextColor};
`;

function Video(props) {
  const {
    children,
    timeToStop,
    vimeoID,
    overlayColor = "transparent",
    endVideoView,
    loop = false,
    controls = false,
    muted = false,
    isBackgroundVideo = true,
    isPaused = true,
    headingText,
    setIsPaused,
    withOverlay,
    height,
    minHeight,
    isPauseable = true,
    videoHeroType,
    overlayBackground,
    volumeToggle = false,
  } = props;

  const [counter, setCounter] = useState(0);
  const currentTimestamp = Math.floor(counter / 4);
  const isTimeToStop = currentTimestamp === timeToStop;

  const VideoWrapperComponent = isBackgroundVideo
    ? StyledPlayerWrapper
    : StyledResponsiveWrapper;

  const [hasVolume, setHasVolume] = useState(!isPaused ? 0 : 1);

  return (
    <VideoWrapperComponent
      $height={height}
      data-testid="vimeo-video-component"
      isPauseable={isPauseable}
      $minHeight={minHeight}
      $videoHeroType={videoHeroType}
      className="vimeo-video-component-wrapper"
    >
      {vimeoID && (
        <>
          <Vimeo
            autoplay={!isPaused}
            controls={controls}
            loop={loop}
            muted={!isPaused || muted}
            onTimeUpdate={() => setCounter((curr) => curr + 1)}
            paused={isPaused || isTimeToStop}
            responsive={!isBackgroundVideo}
            video={vimeoID}
            volume={hasVolume}
          />
          {volumeToggle && (
            <StyledVolumeWrapper>
              {hasVolume === 0 ? (
                <Icon
                  icon="speakerOff"
                  onClick={() => {
                    setHasVolume(1);
                  }}
                  size={28}
                />
              ) : (
                <Icon
                  icon="speakerOn"
                  onClick={() => {
                    setHasVolume(0);
                  }}
                  size={28}
                />
              )}
            </StyledVolumeWrapper>
          )}
        </>
      )}
      {withOverlay && (
        <VideoOverlay
          isPauseable={isPauseable}
          isPaused={isPaused}
          overlayBackground={overlayBackground}
          overlayColor={overlayColor}
          setIsPaused={setIsPaused}
          videoHeroType={videoHeroType}
        >
          {headingText && (
            <StyledContentWrapper className="d-flex justify-center align-items-center flex-column mt-5 video-over-header-text">
              {headingText}
            </StyledContentWrapper>
          )}
        </VideoOverlay>
      )}
      {children}
      {isTimeToStop && endVideoView}
    </VideoWrapperComponent>
  );
}

Video.defaultProps = {
  children: null,
  controls: false,
  endVideoView: null,
  headingText: null,
  height: null,
  isBackgroundVideo: true,
  isPauseable: true,
  isPaused: true,
  loop: false,
  minHeight: null,
  muted: false,
  overlayBackground: "rgba(0, 0, 0, 0.25)",
  overlayColor: "transparent",
  setIsPaused: null,
  timeToStop: null,
  volumeToggle: false,
  withOverlay: true,
  vimeoID: null,
  videoHeroType: "classic",
};

Video.propTypes = {
  children: PropTypes.element,
  controls: PropTypes.bool,
  endVideoView: PropTypes.element,
  headingText: PropTypes.element,
  height: PropTypes.string,
  isBackgroundVideo: PropTypes.bool,
  isPauseable: PropTypes.bool,
  isPaused: PropTypes.bool,
  loop: PropTypes.bool,
  minHeight: PropTypes.string,
  muted: PropTypes.bool,
  overlayBackground: PropTypes.string,
  overlayColor: PropTypes.string,
  setIsPaused: PropTypes.func,
  timeToStop: PropTypes.number,
  videoHeroType: PropTypes.string,
  vimeoID: PropTypes.string,
  volumeToggle: PropTypes.bool,
  withOverlay: PropTypes.bool,
};

export default observer(Video);
